import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL, CATEGORY_NAME, fmtDuration } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { ArrowLeft, Download, Play, Pencil, Sparkles, LogOut, Check, X, RefreshCw, Film, Share2, Facebook, Twitter, Link2, BarChart3, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AutocompleteInput from "@/components/AutocompleteInput";

export default function ClientGalleryDetail() {
  const { galleryId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewClip, setPreviewClip] = useState(null);
  const [renameClip, setRenameClip] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);

  const refresh = async () => {
    try {
      const [g, r] = await Promise.all([
        api.get(`/galleries/${galleryId}`),
        api.get(`/galleries/${galleryId}/reels`),
      ]);
      setGallery(g.data.gallery);
      setReels(r.data.reels || []);
    } catch (e) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [galleryId]);
  // Poll reels every 5s while any are queued/processing
  useEffect(() => {
    const active = reels.some((r) => ["queued", "processing"].includes(r.status));
    if (!active) return;
    const id = setInterval(async () => {
      const r = await api.get(`/galleries/${galleryId}/reels`);
      setReels(r.data.reels || []);
    }, 5000);
    return () => clearInterval(id);
  }, [reels, galleryId]);

  if (loading) return <div className="p-12 text-stone-500">Loading…</div>;
  if (!gallery) return <div className="p-12 text-stone-500">Gallery not found.</div>;

  const clips = gallery.clips || [];

  return (
    <div className="min-h-screen bg-stone-950" data-testid="client-gallery-page">
      <header className="border-b border-stone-800">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/portal" className="text-stone-400 hover:text-stone-100 text-xs uppercase tracking-widest inline-flex items-center gap-1" data-testid="portal-back">
            <ArrowLeft size={12} /> All Galleries
          </Link>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="btn-secondary inline-flex items-center gap-1.5 text-xs"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-8">
        <div className="border-l-4 border-orange-600 pl-5 mb-6">
          <div className="label-tiny mb-2">Gallery</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold leading-[0.95]">{gallery.name}</h1>
          {gallery.description && <p className="text-stone-400 mt-3 max-w-2xl">{gallery.description}</p>}
        </div>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="text-stone-400 text-sm">
            <span className="text-orange-400 font-mono-tc text-lg">{clips.length}</span> clip{clips.length === 1 ? "" : "s"}
          </div>
          {clips.length > 0 && (
            <button
              onClick={() => setShowBuilder(true)}
              className="btn-primary inline-flex items-center gap-2 ml-auto"
              data-testid="build-reel-btn"
            >
              <Sparkles size={13} /> Build a Highlight Reel
            </button>
          )}
        </div>

        {clips.length === 0 ? (
          <div className="border border-dashed border-stone-800 p-12 text-center text-stone-500">
            No clips in this gallery yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="client-clips-grid">
            {clips.map((c) => (
              <div key={c.id} className="border border-stone-800 hover:border-orange-700 group" data-testid={`client-clip-${c.id}`}>
                <button
                  onClick={() => setPreviewClip(c)}
                  className="block w-full aspect-video bg-black relative overflow-hidden"
                >
                  <img
                    src={`${BACKEND_URL}/api/clips/${c.id}/thumbnail`}
                    loading="lazy"
                    alt={c.sub_label || "clip"}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 flex items-center justify-center">
                    <Play size={28} className="text-white" fill="white" />
                  </div>
                  {c.category && (
                    <div className="absolute top-2 left-2">
                      <span className="tag-cat backdrop-blur-sm bg-stone-900/80">{CATEGORY_NAME[c.category]}</span>
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 font-mono-tc text-xs text-orange-400 bg-black/70 px-2 py-0.5">
                    {fmtDuration(c.duration)}
                  </div>
                </button>
                <div className="p-3">
                  <div className="font-display uppercase font-semibold text-sm leading-tight truncate" title={c.rider_horse_name || c.sub_label}>
                    {c.rider_horse_name || c.sub_label || "Untitled"}
                  </div>
                  {c.rider_horse_name && c.sub_label && (
                    <div className="text-[11px] uppercase tracking-wider text-orange-400/90 mt-0.5 truncate">{c.sub_label}</div>
                  )}
                  <div className="mt-2 pt-2 border-t border-stone-800 flex items-center gap-1 flex-wrap">
                    <a
                      href={`${BACKEND_URL}/api/clips/${c.id}/download`}
                      className="btn-secondary !py-1.5 !px-2 inline-flex items-center gap-1.5 text-[11px]"
                      data-testid={`client-download-${c.id}`}
                      title="Download"
                    >
                      <Download size={11} /> Download
                    </a>
                    <ShareClipButton clipId={c.id} clipName={c.rider_horse_name || c.sub_label || "Clip"} />
                    <button
                      onClick={() => setRenameClip(c)}
                      className="btn-secondary !py-1.5 !px-2 ml-auto"
                      title="Rename"
                      data-testid={`client-rename-${c.id}`}
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {reels.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display uppercase text-2xl font-bold mb-4">Your Reels</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="client-reels-grid">
              {reels.map((r) => (
                <ReelCard key={r.id} reel={r} onDeleted={() => setReels((rs) => rs.filter((x) => x.id !== r.id))} />
              ))}
            </div>
          </section>
        )}
      </main>

      {previewClip && (
        <PreviewModal clip={previewClip} user={user} onClose={() => setPreviewClip(null)} />
      )}
      {renameClip && (
        <RenameModal
          clip={renameClip}
          galleryId={galleryId}
          onClose={() => setRenameClip(null)}
          onSaved={(name) => {
            setGallery((g) => ({
              ...g,
              clips: g.clips.map((c) => c.id === renameClip.id ? { ...c, sub_label: name } : c),
            }));
            setRenameClip(null);
          }}
        />
      )}
      {showBuilder && (
        <ReelBuilderModal
          gallery={gallery}
          onClose={() => setShowBuilder(false)}
          onBuilt={(reel) => {
            setReels((r) => [reel, ...r]);
            setShowBuilder(false);
            toast.success("Reel is rendering — check back soon!");
          }}
        />
      )}
    </div>
  );
}

const REACTION_OPTIONS = [
  { key: "fire", emoji: "🔥", label: "Fire" },
  { key: "clap", emoji: "👏", label: "Clap" },
  { key: "heart", emoji: "❤️", label: "Heart" },
  { key: "wow", emoji: "🤠", label: "Cowboy" },
  { key: "rope", emoji: "🪢", label: "Rope" },
];

function PreviewModal({ clip, onClose, user }) {
  const [counts, setCounts] = useState({});
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const authorName = user?.name || user?.email?.split("@")[0] || "Guest";

  const refresh = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get(`/clips/${clip.id}/reactions`),
        api.get(`/clips/${clip.id}/comments`),
      ]);
      setCounts(r.data.counts || {});
      setComments(c.data.comments || []);
    } catch (e) {/* ignore */}
  };

  useEffect(() => {
    refresh();
    // Register one view
    api.post(`/clips/${clip.id}/view`).catch(() => {});
  }, [clip.id]);

  const react = async (key) => {
    try {
      const r = await api.post(`/clips/${clip.id}/reactions`, { emoji: key, by_name: authorName });
      setCounts(r.data.counts || {});
    } catch (e) { toast.error("Failed to react"); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || posting) return;
    setPosting(true);
    try {
      const r = await api.post(`/clips/${clip.id}/comments`, { text: t, by_name: authorName });
      setComments((cs) => [r.data.comment, ...cs]);
      setText("");
    } catch (e) {
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments((cs) => cs.filter((c) => c.id !== id));
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-start sm:items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display uppercase font-bold text-xl">{clip.rider_horse_name || clip.sub_label || "Clip"}</div>
            {clip.sub_label && clip.rider_horse_name && <div className="text-sm text-orange-400/80">{clip.sub_label}</div>}
          </div>
          <button onClick={onClose} className="text-stone-300 hover:text-white"><X size={20} /></button>
        </div>
        <video src={clip.stream_url} controls autoPlay className="w-full bg-black" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a href={`${BACKEND_URL}/api/clips/${clip.id}/download`} className="btn-secondary inline-flex items-center gap-2 text-xs">
            <Download size={11} /> Download
          </a>
          <div className="flex gap-1 ml-auto" data-testid="reactions-bar">
            {REACTION_OPTIONS.map((r) => (
              <button
                key={r.key}
                onClick={() => react(r.key)}
                title={r.label}
                className="px-2.5 py-1.5 border border-stone-800 hover:border-orange-600 text-sm transition-colors inline-flex items-center gap-1.5"
                data-testid={`reaction-${r.key}`}
              >
                <span>{r.emoji}</span>
                <span className="font-mono-tc text-stone-400 text-xs">{counts[r.key] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 border-t border-stone-800 pt-4">
          <h3 className="font-display uppercase text-lg font-bold mb-3 inline-flex items-center gap-2">
            <MessageSquare size={14} /> Comments <span className="text-stone-500 font-mono-tc text-sm">({comments.length})</span>
          </h3>
          <form onSubmit={postComment} className="flex gap-2 mb-4" data-testid="comment-form">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Comment as ${authorName}…`}
              className="field-input flex-1"
              maxLength={2000}
              data-testid="comment-input"
            />
            <button type="submit" disabled={posting || !text.trim()} className="btn-primary text-xs whitespace-nowrap" data-testid="comment-submit">
              {posting ? "Posting…" : "Post"}
            </button>
          </form>
          {comments.length === 0 ? (
            <div className="text-stone-500 text-sm py-4 text-center">No comments yet — be the first.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="comments-list">
              {comments.map((cm) => (
                <div key={cm.id} className="border border-stone-800 p-3" data-testid={`comment-${cm.id}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-xs uppercase tracking-widest text-orange-400/80">{cm.by_name || "Anonymous"}</div>
                    <div className="flex items-center gap-3">
                      <div className="text-[10px] text-stone-600">{(cm.created_at || "").slice(0, 10)}</div>
                      <button onClick={() => del(cm.id)} className="text-stone-600 hover:text-rose-400" title="Delete">
                        <X size={11} />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-stone-300 whitespace-pre-wrap">{cm.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RenameModal({ clip, galleryId, onClose, onSaved }) {
  const [name, setName] = useState(clip.sub_label || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/galleries/${galleryId}/clips/${clip.id}/name`, { name });
      toast.success("Renamed");
      onSaved(name);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="rename-modal">
        <h2 className="font-display uppercase text-xl font-bold mb-4">Rename Clip</h2>
        <form onSubmit={submit} className="space-y-3">
          <AutocompleteInput field="sub_label" value={name} onChange={setName} className="field-input w-full" autoFocus data-testid="rename-input" />
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="rename-submit">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReelBuilderModal({ gallery, onClose, onBuilt }) {
  const [title, setTitle] = useState(`${gallery.name} Highlights`);
  const [picked, setPicked] = useState(new Set((gallery.clips || []).map((c) => c.id)));
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(gallery.reel_template_id || "");

  useEffect(() => {
    api.get("/client-reel-templates", { params: { gallery_id: gallery.id } })
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => {});
  }, [gallery.id]);

  const toggle = (id) => {
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (picked.size === 0) { toast.error("Pick at least one clip"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/galleries/${gallery.id}/reels`, {
        title: title.trim() || "Highlight Reel",
        clip_ids: Array.from(picked),
        template_id: templateId || null,
      });
      onBuilt(r.data.reel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build reel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-4 py-8 overflow-auto" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="reel-builder-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display uppercase text-2xl font-bold">Build Highlight Reel</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="label-tiny mb-1">Reel Title</div>
            <AutocompleteInput field="reel_title" value={title} onChange={setTitle} className="field-input w-full" data-testid="reel-title" />
          </div>
          <div>
            <div className="label-tiny mb-1">Branded Template</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto" data-testid="reel-template-grid">
              <button
                type="button"
                onClick={() => setTemplateId("")}
                className={`text-left p-3 border text-xs transition-colors ${!templateId ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                data-testid="reel-template-none"
              >
                <div className="font-display uppercase font-semibold">No Template</div>
                <div className="text-stone-500 mt-0.5">Hard cuts, no intro card.</div>
              </button>
              {templates.map((t) => {
                const on = t.id === templateId;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setTemplateId(t.id)}
                    className={`text-left p-3 border text-xs transition-colors ${on ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                    data-testid={`reel-template-${t.id}`}
                  >
                    <div className="font-display uppercase font-semibold">{t.name}</div>
                    <div className="text-stone-500 mt-0.5 line-clamp-2">{t.description || `${t.transition_style} transitions${t.intro_card_text ? " · intro card" : ""}`}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="label-tiny mb-2">Pick clips ({picked.size} selected)</div>
            <div className="max-h-[40vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 border border-stone-800 p-2">
              {(gallery.clips || []).map((c) => {
                const on = picked.has(c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    className={`text-left border ${on ? "border-orange-600" : "border-stone-800 hover:border-stone-700"}`}
                    data-testid={`reel-pick-${c.id}`}
                  >
                    <div className="aspect-video bg-black relative overflow-hidden">
                      {c.stream_url && <video src={c.stream_url} muted preload="metadata" className="w-full h-full object-cover" />}
                      {on && (
                        <div className="absolute inset-0 bg-orange-600/20 flex items-center justify-center">
                          <div className="bg-orange-600 text-white rounded-full p-1.5"><Check size={12} /></div>
                        </div>
                      )}
                    </div>
                    <div className="p-1.5 text-[11px] truncate">{c.rider_horse_name || c.sub_label || "Untitled"}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <button type="submit" disabled={busy || picked.size === 0} className="btn-primary w-full" data-testid="reel-build-submit">
            <Sparkles size={13} className="inline mr-2" />
            {busy ? "Rendering…" : `Build reel from ${picked.size} clip${picked.size === 1 ? "" : "s"}`}
          </button>
        </form>
      </div>
    </div>
  );
}

function ReelCard({ reel, onDeleted }) {
  const isReady = (reel.status === "done" || reel.status === "ready") && reel.stream_url;
  const [deleting, setDeleting] = useState(false);
  const viewedRef = useState({ done: false })[0];

  const recordView = () => {
    if (viewedRef.done) return;
    viewedRef.done = true;
    api.post(`/reels/${reel.id}/view`).catch(() => {});
  };

  const del = async () => {
    if (!window.confirm(`Delete reel "${reel.title}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/reels/${reel.id}`);
      toast.success("Reel deleted");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-stone-800" data-testid={`reel-card-${reel.id}`}>
      <div className="aspect-video bg-black relative">
        {isReady ? (
          <video src={reel.stream_url} controls preload="metadata" className="w-full h-full" onPlay={recordView} />
        ) : reel.status === "failed" ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-rose-400 text-sm">
            <X size={20} /> Failed
            <div className="text-xs text-stone-500 mt-1 px-3 text-center line-clamp-2">{reel.error}</div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 text-sm">
            <RefreshCw size={20} className="animate-spin mb-2" />
            {reel.status} · {reel.progress || 0}%
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="font-display uppercase font-semibold truncate">{reel.title}</div>
        <div className="flex items-center gap-2 mt-2">
          {isReady && (
            <a href={reel.stream_url} download className="btn-secondary inline-flex items-center gap-1.5 text-xs">
              <Download size={11} /> Download
            </a>
          )}
          <button
            onClick={del}
            disabled={deleting}
            className="btn-secondary !py-1.5 !px-2 ml-auto hover:!text-rose-500"
            title="Delete reel"
            data-testid={`reel-delete-${reel.id}`}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}


function ShareClipButton({ clipId, clipName }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (url) return url;
    setBusy(true);
    try {
      const r = await api.post(`/clips/${clipId}/share`);
      const full = `${window.location.origin}/share/${r.data.token}`;
      setUrl(full);
      return full;
    } catch (e) {
      toast.error("Failed to create share link");
      return "";
    } finally {
      setBusy(false);
    }
  };

  const openMenu = async () => {
    if (!url) await create();
    setOpen(true);
  };

  const copy = async () => {
    const u = url || (await create());
    if (!u) return;
    await navigator.clipboard.writeText(u);
    toast.success("Link copied — paste in Facebook / Instagram / anywhere");
  };

  const openShareUrl = async (kind) => {
    const u = url || (await create());
    if (!u) return;
    const enc = encodeURIComponent(u);
    const msg = encodeURIComponent(clipName ? `Check out my ${clipName} run!` : "Check out my run!");
    let target = "";
    if (kind === "facebook") target = `https://www.facebook.com/sharer/sharer.php?u=${enc}`;
    if (kind === "twitter") target = `https://twitter.com/intent/tweet?url=${enc}&text=${msg}`;
    if (kind === "whatsapp") target = `https://wa.me/?text=${msg}%20${enc}`;
    if (target) window.open(target, "_blank", "width=600,height=600");
  };

  return (
    <div className="relative">
      <button
        onClick={openMenu}
        disabled={busy}
        className="btn-secondary !py-1.5 !px-2 hover:!text-orange-400"
        title="Share"
        data-testid={`client-share-${clipId}`}
      >
        <Share2 size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 bottom-full mb-1 w-56 bg-stone-950 border border-stone-700 shadow-xl z-30 p-2" data-testid={`share-popover-${clipId}`}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-800">Share this clip</div>
            <button onClick={copy} className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 text-stone-300" data-testid={`share-copy-${clipId}`}>
              <Link2 size={12} /> Copy link
            </button>
            <button onClick={() => openShareUrl("facebook")} className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 text-stone-300" data-testid={`share-fb-${clipId}`}>
              <Facebook size={12} /> Facebook
            </button>
            <button onClick={() => openShareUrl("twitter")} className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 text-stone-300" data-testid={`share-tw-${clipId}`}>
              <Twitter size={12} /> Twitter / X
            </button>
            <button onClick={() => openShareUrl("whatsapp")} className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-stone-900 text-stone-300" data-testid={`share-wa-${clipId}`}>
              <MessageSquare size={12} /> WhatsApp
            </button>
            {url && (
              <div className="border-t border-stone-800 mt-1 pt-1 px-2 text-[10px] text-stone-600 break-all">{url}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
